import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: 'sm' | 'md' | 'lg';
  /**
   * Announced to screen readers. Defaults to "Loading". Pass something specific
   * when the spinner replaces a whole screen ("Checking your session").
   */
  label?: string;
}

const SIZES = {
  sm: 'size-4 border-2',
  md: 'size-6 border-2',
  lg: 'size-9 border-[3px]',
} as const;

/**
 * A ring, not a set of dots: a partial ring reads as motion even at 4px.
 * `data-medtrack-spinner` is the hook the reduced-motion block in index.css
 * uses to slow this down rather than freeze it — a frozen spinner reads as a
 * broken image.
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { className, size = 'md', label = 'Loading', ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      role="status"
      aria-live="polite"
      className={cn('inline-flex items-center justify-center', className)}
      {...props}
    >
      <span
        data-medtrack-spinner=""
        aria-hidden="true"
        className={cn(
          'animate-spin rounded-full border-current border-r-transparent opacity-70',
          SIZES[size],
        )}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
});
